import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw, Maximize, Volume2, VolumeX } from 'lucide-react';
import { SCENES, PRESENTATION, TOTAL_SECONDS } from '../presentationConfig';
import { useClock } from '../engine/useClock';
import { Stage, fmtClock } from '../engine/Stage';
import { usePreload, ReadyScreen } from '../engine/Preloader';
import { usePresentationKeys, toggleFullscreen, scenePoster } from '../engine/controls';

// Approver view (/watch): passcode → assets load → the deck simply plays. No notes, script, or control centre.
export default function ViewerMode() {
  const preload = usePreload(true);
  const [began, setBegan] = useState(false);
  const [audioOn, setAudioOn] = useState(PRESENTATION.audio.defaultOn);
  const [controls, setControls] = useState(true);
  const [strip, setStrip] = useState(false);
  const idle = useRef(null);
  const clock = useClock({ scenes: SCENES, duration: PRESENTATION.sceneDuration, initialIndex: 0, autoplay: false, speed: 1 });

  const begin = useCallback(() => {
    setBegan(true);
    clock.play();
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.().catch(() => {});
  }, [clock]);

  const poke = useCallback((e) => {
    setControls(true);
    if (e && typeof e.clientY === 'number') setStrip(e.clientY > window.innerHeight - 190);
    clearTimeout(idle.current);
    idle.current = setTimeout(() => setControls(false), 2500);
  }, []);
  useEffect(() => { poke(); return () => clearTimeout(idle.current); }, [poke]);

  const keys = useMemo(() => ({
    next: clock.next, prev: clock.prev, toggle: clock.toggle, restart: clock.restart,
    fullscreen: () => toggleFullscreen(), audio: () => setAudioOn((v) => !v),
  }), [clock.next, clock.prev, clock.toggle, clock.restart]);
  usePresentationKeys(keys, began);

  // Hover-revealed filmstrip so an approver can jump between slides.
  const stripRef = useRef(null);
  const scene = SCENES[clock.index];
  const jump = useCallback((i) => { clock.goTo(i); clock.play(); }, [clock]);
  useEffect(() => {
    const el = stripRef.current?.querySelector('.pk-film.active');
    if (el && strip) el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [clock.index, strip]);

  if (!began) {
    return <ReadyScreen preload={preload} title={PRESENTATION.title} subtitle={`${SCENES.length} scenes · ${fmtClock(TOTAL_SECONDS)}`} onBegin={begin} autoBegin />;
  }

  return (
    <div className={`pk-present pk-viewer ${controls ? '' : 'pk-idle'}`} onMouseMove={poke} onTouchStart={poke} data-testid="pk-viewer-mode">
      <Stage scenes={SCENES} clock={clock} showTimer={false} mode="present" audioOn={audioOn} onClick={clock.toggle} />
      <div className={`pk-viewer-dock ${controls ? 'show' : ''} ${strip ? 'open' : ''}`} onMouseEnter={() => setStrip(true)} onMouseLeave={() => setStrip(false)} onClick={(e) => e.stopPropagation()} data-testid="pk-viewer-dock">
        <nav className="pk-filmstrip pk-viewer-strip" ref={stripRef} data-testid="pk-viewer-filmstrip">
          {SCENES.map((s, i) => {
            const poster = scenePoster(s);
            return (
              <button key={s.id} className={`pk-film ${i === clock.index ? 'active' : ''} ${i < clock.index ? 'done' : ''}`} onClick={() => jump(i)} data-testid={`pk-viewer-film-${s.n}`}>
                <div className="pk-film-thumb">{poster ? <img src={poster} alt="" loading="lazy" /> : <div className="pk-film-blank" />}<span>{String(s.n).padStart(2, '0')}</span></div>
                <div className="pk-film-title">{s.title}</div>
              </button>
            );
          })}
        </nav>
        <div className="pk-controls pk-controls-min show" data-testid="pk-viewer-controls">
          <div className="pk-controls-buttons">
            <button onClick={clock.prev} title="Previous (←)" data-testid="pk-viewer-prev"><ChevronLeft size={20} /></button>
            <button onClick={clock.toggle} title="Pause / resume (space)" className="pk-ctrl-main" data-testid="pk-viewer-toggle">{clock.playing ? <Pause size={20} /> : <Play size={20} />}</button>
            <button onClick={clock.next} title="Next (→)" data-testid="pk-viewer-next"><ChevronRight size={20} /></button>
            <span className="pk-ctrl-sep" />
            <button onClick={clock.restart} title="Restart" data-testid="pk-viewer-restart"><RotateCcw size={18} /></button>
            <button onClick={() => setAudioOn((v) => !v)} title="Sound" className={audioOn ? 'on' : ''} data-testid="pk-viewer-audio">{audioOn ? <Volume2 size={18} /> : <VolumeX size={18} />}</button>
            <button onClick={() => toggleFullscreen()} title="Fullscreen (F)" data-testid="pk-viewer-fullscreen"><Maximize size={18} /></button>
          </div>
          <div className="pk-controls-time"><small><b>{String(clock.index + 1).padStart(2, '0')}</b> {scene.title} · {fmtClock(clock.overall)} / {fmtClock(TOTAL_SECONDS)}</small></div>
        </div>
      </div>
    </div>
  );
}
